import { useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { adminStyles as baseAdminStyles } from '../styles/adminStyles';

export function getAdminColors(isDarkMode, theme) {
  const palette = isDarkMode
    ? {
      pageBg: '#000000',
      sidebarBg: '#0d0d0d',
      sidebarBorder: 'rgba(255,255,255,0.08)',
      sidebarCategoryActive: 'rgba(46, 204, 113, 0.1)',
      textPrimary: theme.text.primary,
      textSecondary: theme.text.secondary,
      textMuted: theme.text.muted,
      textOnAccent: '#ffffff',
      cardBg: 'rgba(255,255,255,0.08)',
      cardBgSubtle: 'rgba(255,255,255,0.05)',
      cardBorder: 'rgba(255,255,255,0.12)',
      inputBg: 'rgba(255,255,255,0.1)',
      inputBorder: 'rgba(255,255,255,0.2)',
      inputText: '#ffffff',
      inputPlaceholder: 'rgba(255,255,255,0.45)',
      sectionAccent: 'rgba(11, 181, 223, 0.1)',
      sectionAccentBorder: 'rgba(11, 181, 223, 0.3)',
      navActive: '#2ecc71',
      navItemText: 'rgba(255,255,255,0.78)',
      statAccent: '#0bb5df',
      link: theme.text.link,
      emptyState: theme.text.secondary,
      overlayBg: 'rgba(0,0,0,0.3)',
      divider: 'rgba(255,255,255,0.12)',
      iconMuted: 'rgba(255,255,255,0.65)',
      iconSubtle: 'rgba(255,255,255,0.45)',
      shadowColor: 'rgba(0,0,0,0.25)',
      accentGreen: '#2ecc71',
    }
    : {
      pageBg: theme.background.primary,
      sidebarBg: theme.background.secondary,
      sidebarBorder: theme.ui.border,
      sidebarCategoryActive: 'rgba(39, 174, 96, 0.12)',
      textPrimary: theme.text.primary,
      textSecondary: theme.text.secondary,
      textMuted: theme.text.muted,
      textOnAccent: '#ffffff',
      cardBg: theme.background.card,
      cardBgSubtle: theme.background.tertiary,
      cardBorder: theme.ui.borderStrong,
      inputBg: theme.input.background,
      inputBorder: theme.input.border,
      inputText: theme.input.text,
      inputPlaceholder: theme.text.secondary,
      sectionAccent: 'rgba(52, 152, 219, 0.08)',
      sectionAccentBorder: 'rgba(52, 152, 219, 0.28)',
      navActive: '#27ae60',
      navItemText: theme.text.secondary,
      statAccent: '#2980b9',
      link: theme.text.link,
      emptyState: theme.text.secondary,
      overlayBg: 'rgba(0,0,0,0.04)',
      divider: theme.ui.divider,
      iconMuted: theme.text.secondary,
      iconSubtle: theme.text.muted,
      shadowColor: theme.ui.shadow,
      accentGreen: '#27ae60',
    };

  return { ...palette, isDarkMode };
}

function patch(styles, key, override) {
  if (!baseAdminStyles[key]) return;
  styles[key] = [baseAdminStyles[key], override];
}

/** Flat merge for styles applied to native DOM elements (e.g. <select>) — arrays crash React DOM. */
function patchFlat(styles, key, override) {
  if (!baseAdminStyles[key]) return;
  styles[key] = { ...baseAdminStyles[key], ...override };
}

function buildThemedAdminStyles(colors) {
  const styles = { ...baseAdminStyles };

  patch(styles, 'pageTitle', { color: colors.textPrimary });
  patch(styles, 'pageSubtitle', { color: colors.textSecondary });
  patch(styles, 'emptyStateText', { color: colors.emptyState });
  patch(styles, 'sectionTitle', { color: colors.textPrimary });
  patch(styles, 'cardTitle', { color: colors.textPrimary });
  patch(styles, 'userName', { color: colors.textPrimary });
  patch(styles, 'userRole', { color: colors.textSecondary });
  patch(styles, 'userWorkplace', { color: colors.textSecondary });
  patch(styles, 'userJoinDate', { color: colors.textMuted });
  patch(styles, 'userSubmissions', { color: colors.textMuted });
  patch(styles, 'detailText', { color: colors.textPrimary });
  patch(styles, 'detailLabel', { color: colors.textSecondary });
  patch(styles, 'detailValue', { color: colors.textPrimary });
  patch(styles, 'notesLabel', { color: colors.textSecondary });
  patch(styles, 'notesText', { color: colors.textPrimary });
  patch(styles, 'devModeText', { color: colors.textSecondary, opacity: 1 });
  patch(styles, 'devModeSection', {
    backgroundColor: colors.sectionAccent,
    borderColor: colors.sectionAccentBorder,
  });
  patchFlat(styles, 'userAnalyticsContainer', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patchFlat(styles, 'notesSection', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patchFlat(styles, 'sectionContainer', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patchFlat(styles, 'manualItem', {
    backgroundColor: colors.cardBg,
    borderColor: colors.cardBorder,
  });
  patchFlat(styles, 'manualName', { color: colors.textPrimary });
  patchFlat(styles, 'manualUrl', { color: colors.textSecondary });
  patchFlat(styles, 'manualsCount', { color: colors.textSecondary });
  patchFlat(styles, 'softwareUrl', { color: colors.textMuted });
  patchFlat(styles, 'softwareInfo', { borderTopColor: colors.divider });
  patchFlat(styles, 'infoText', { color: colors.textPrimary });
  patchFlat(styles, 'infoListItem', { color: colors.textSecondary });
  patchFlat(styles, 'progressText', { color: colors.textPrimary });
  patchFlat(styles, 'fileUploadLabel', { color: colors.textPrimary });
  patchFlat(styles, 'importModeDescription', { color: colors.textSecondary });
  patchFlat(styles, 'modeButton', {
    backgroundColor: colors.cardBgSubtle,
    borderColor: colors.cardBorder,
  });
  patchFlat(styles, 'modeButtonText', { color: colors.textPrimary });
  patchFlat(styles, 'manualActionText', { color: colors.textOnAccent });
  patchFlat(styles, 'editInput', {
    backgroundColor: colors.inputBg,
    color: colors.inputText,
    borderColor: colors.inputBorder,
  });
  patchFlat(styles, 'devModeSelect', {
    backgroundColor: colors.inputBg,
    color: colors.inputText,
    borderColor: colors.inputBorder,
  });
  patch(styles, 'reportCard', {
    backgroundColor: colors.cardBg,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'submissionDetails', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'venueInfoCard', {
    backgroundColor: colors.cardBg,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    boxShadow: `0px 2px 8px ${colors.shadowColor}`,
  });
  patch(styles, 'userAnalyticCard', {
    backgroundColor: colors.cardBg,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    boxShadow: `0px 2px 8px ${colors.shadowColor}`,
  });
  patch(styles, 'userAnalyticNumber', { color: colors.statAccent, textShadow: 'none' });
  patch(styles, 'userAnalyticLabel', { color: colors.textPrimary, opacity: 1 });
  patch(styles, 'userSearchContainer', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'userSearchInput', {
    backgroundColor: colors.inputBg,
    color: colors.inputText,
    border: `1px solid ${colors.inputBorder}`,
  });
  patch(styles, 'searchInput', {
    backgroundColor: colors.inputBg,
    color: colors.inputText,
    borderColor: colors.inputBorder,
  });
  patch(styles, 'formInput', {
    backgroundColor: colors.inputBg,
    color: colors.inputText,
    borderColor: colors.inputBorder,
  });
  patch(styles, 'formTitle', { color: colors.textPrimary });
  patch(styles, 'manualForm', {
    backgroundColor: colors.cardBgSubtle,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'manualsCount', { color: colors.textSecondary });
  patch(styles, 'mediaSection', { backgroundColor: colors.overlayBg });
  patch(styles, 'mediaTitle', { color: colors.textPrimary });
  patch(styles, 'legendContainer', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'legendSection', {
    backgroundColor: colors.cardBg,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'tab', {
    backgroundColor: colors.cardBgSubtle,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'tabText', { color: colors.textSecondary });
  patch(styles, 'stylePageTitle', { color: colors.textPrimary });
  patch(styles, 'sectionTitleText', { color: colors.textPrimary });
  patch(styles, 'statusButtonText', { color: colors.textPrimary });
  patch(styles, 'filterLabel', { color: colors.textSecondary });
  patch(styles, 'link', { color: colors.link });
  patch(styles, 'userEmail', { color: colors.textSecondary });
  patch(styles, 'bioSection', { backgroundColor: colors.overlayBg });
  patch(styles, 'bioTitle', { color: colors.textPrimary });
  patch(styles, 'uploadButtonText', { color: colors.textOnAccent });
  patch(styles, 'venueCard', {
    backgroundColor: colors.cardBg,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    minHeight: 380,
    height: 'auto',
  });
  patch(styles, 'venueName', {
    color: colors.textPrimary,
    textShadow: colors.isDarkMode ? '0px 2px 4px rgba(0, 0, 0, 0.5)' : 'none',
    fontSize: 22,
    textAlign: 'left',
  });
  patch(styles, 'loadMoreButton', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'loadMoreButtonText', { color: colors.textPrimary });
  patch(styles, 'discussionBoardSection', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'discussionToggle', {
    backgroundColor: colors.cardBg,
    borderBottomColor: colors.divider,
  });
  patch(styles, 'discussionToggleText', { color: colors.textPrimary });
  patch(styles, 'discussionTitle', { color: colors.textPrimary });
  patch(styles, 'adminMessageCard', {
    backgroundColor: colors.cardBg,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'adminMessageUser', { color: colors.textPrimary });
  patch(styles, 'adminMessageTime', { color: colors.textMuted });
  patch(styles, 'adminMessageText', { color: colors.textSecondary });
  patch(styles, 'noMessagesText', { color: colors.textMuted });
  patch(styles, 'loadingText', { color: colors.textSecondary });
  patch(styles, 'refreshButton', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'refreshButtonText', { color: colors.textPrimary });
  patch(styles, 'actionButtonText', { color: colors.textOnAccent });
  patch(styles, 'venueCardContent', {
    backgroundColor: colors.isDarkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.92)',
  });
  patch(styles, 'venueLocation', {
    color: colors.isDarkMode ? 'rgba(255, 255, 255, 0.8)' : colors.textSecondary,
  });
  patch(styles, 'venuePhotoCount', {
    color: colors.isDarkMode ? 'rgba(255, 255, 255, 0.6)' : colors.textMuted,
  });

  // Report cards — venue reports, resources, submissions
  patch(styles, 'profileCard', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'submissionCard', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'gradesCard', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'detailedGradesCard', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'gradeItem', {
    backgroundColor: colors.overlayBg,
  });
  patch(styles, 'gradeLabel', { color: colors.textSecondary });
  patch(styles, 'gradeLetter', { color: colors.textPrimary });
  patch(styles, 'overallGrade', { color: colors.textPrimary });
  patch(styles, 'gradeItemLabel', { color: colors.textPrimary });
  patch(styles, 'gradeItemValue', { color: colors.textPrimary });
  patch(styles, 'reportCount', { color: colors.textMuted });
  patch(styles, 'venueTitle', { color: colors.textPrimary });
  patch(styles, 'venueText', { color: colors.textPrimary });
  patch(styles, 'userFullName', { color: colors.textPrimary });
  patch(styles, 'techSpecsGrid', { backgroundColor: colors.overlayBg });
  patch(styles, 'techSpecItem', { backgroundColor: colors.overlayBg });
  patch(styles, 'techSpecLabel', { color: colors.textSecondary });
  patch(styles, 'techSpecValue', { color: colors.textPrimary });
  patch(styles, 'reportTitleCard', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'reportHeader', {
    borderBottomColor: colors.divider,
  });
  patch(styles, 'detailRow', {
    borderBottomColor: colors.divider,
  });
  patch(styles, 'overallExperience', { color: colors.textPrimary });
  patch(styles, 'mediaLabel', { color: colors.textPrimary });
  patch(styles, 'experienceText', { color: colors.textPrimary });
  patch(styles, 'userStatusText', { color: colors.textPrimary });
  patch(styles, 'resultsCount', { color: colors.textSecondary });

  // Users list filters & legend
  patch(styles, 'filterButton', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'filterButtonText', { color: colors.textPrimary });
  patch(styles, 'legendText', { color: colors.textPrimary });
  patch(styles, 'columnSelector', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'columnSelectorLabel', { color: colors.textPrimary });
  patch(styles, 'columnSelectorButton', {
    backgroundColor: colors.cardBgSubtle,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'columnSelectorButtonActive', {
    backgroundColor: '#0bb5df',
    borderColor: '#0bb5df',
  });
  patch(styles, 'buttonText', { color: colors.textOnAccent });
  patch(styles, 'tabButton', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'tabButtonText', { color: colors.textPrimary });
  patch(styles, 'tabButtonActive', { backgroundColor: '#0bb5df', borderColor: '#0bb5df' });
  patch(styles, 'searchFilterContainer', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });

  // Community moderation
  patch(styles, 'filtersContainer', {
    backgroundColor: colors.cardBgSubtle,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'statCard', {
    backgroundColor: colors.cardBg,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'statNumber', { color: colors.textPrimary });
  patch(styles, 'statLabel', { color: colors.textSecondary });
  patch(styles, 'messageCard', {
    backgroundColor: colors.cardBg,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'messageText', { color: colors.textPrimary });
  patch(styles, 'username', { color: colors.textSecondary });
  patch(styles, 'timestamp', { color: colors.textMuted });
  patch(styles, 'noDataText', { color: colors.textSecondary });
  patch(styles, 'dropdownButton', {
    backgroundColor: colors.inputBg,
    borderColor: colors.inputBorder,
  });
  patch(styles, 'dropdownText', { color: colors.inputText });
  patch(styles, 'deleteButtonText', { color: colors.textOnAccent });

  // Site ops
  patch(styles, 'maintenanceForm', {
    backgroundColor: colors.cardBgSubtle,
    borderColor: colors.cardBorder,
  });
  patch(styles, 'maintenanceInput', {
    backgroundColor: colors.inputBg,
    color: colors.inputText,
    borderColor: colors.inputBorder,
  });
  patch(styles, 'maintenanceScheduleMessage', { color: colors.textMuted, opacity: 1 });
  patch(styles, 'label', { color: colors.textPrimary });

  return styles;
}

/** Plain CSS object for native <select> — never pass RN StyleSheet arrays to DOM elements. */
export function getDomSelectStyle(colors) {
  return {
    backgroundColor: colors.inputBg,
    color: colors.inputText,
    fontFamily: 'VastagoGrotesk, sans-serif',
    fontWeight: '400',
    fontSize: 16,
    padding: 12,
    borderRadius: 8,
    border: `1px solid ${colors.inputBorder}`,
    width: '100%',
    marginBottom: 10,
    cursor: 'pointer',
    outline: 'none',
  };
}

export function useAdminTheme() {
  const { theme, isDarkMode } = useTheme();

  const colors = useMemo(() => getAdminColors(isDarkMode, theme), [isDarkMode, theme]);
  const styles = useMemo(() => buildThemedAdminStyles(colors), [colors]);
  const domSelectStyle = useMemo(() => getDomSelectStyle(colors), [colors]);

  return { theme, isDarkMode, colors, styles, domSelectStyle, baseStyles: baseAdminStyles };
}
